// auth.jsx — first-run onboarding (WelcomeScreen). No accounts (DEC-no-accounts-v2):
// a short questionnaire builds state.profile + units, then "Your plan" recommends
// library programs that fit, with one-tap start, "Build my own" and "Just log
// workouts". A visible Skip on every step lands on Home with no program.
// File name kept for import stability; only exports WelcomeScreen.

import React from 'react';
import { STARTER_PROGRAMS, useApp } from './store.jsx';
import { LL, LLIcon, LLCheck } from './tokens.jsx';
import { useAIStatus } from './ai.js';
import { BackButton } from './phone.jsx';
import { GOALS, LEVELS, EQUIPMENT_TAGS, EQUIPMENT_PRESETS, TEMPLATES, equipmentForPreset } from './program_templates.js';
import { matchPrograms } from './program_match.js';

// The coach-style matcher (src/program_match.js): equipment is a hard
// requirement, beginners get simple programs, days and minutes must fit, and
// every pick carries a plain-language "why".
const rankPlans = (profile) => matchPrograms(profile);
import { TemplateCard, TemplateDetailSheet, SplitStrip, useStartTemplate, PIcon, GoalTile, ProgramStyles, coachProgramAsk } from './programs.jsx';

const kicker = { fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1.2, color: LL.fgDim, textTransform: 'uppercase' };
const meta = { fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute };

const STEPS = ['intro', 'goal', 'experience', 'schedule', 'equipment', 'units', 'injuries', 'plan', 'browse'];
const QUESTION_STEPS = ['goal', 'experience', 'schedule', 'equipment', 'units', 'injuries'];
const SCHEDULE = { 2: ['Mon', 'Thu'], 3: ['Mon', 'Wed', 'Fri'], 4: ['Mon', 'Tue', 'Thu', 'Fri'], 5: ['Mon', 'Tue', 'Wed', 'Fri', 'Sat'], 6: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] };
const MINUTES = [30, 45, 60, 75, 90];
const INJURY_CHIPS = ['Lower back', 'Knees', 'Shoulders', 'Wrists', 'Hips', 'Neck'];

const GOAL_ICON = { hypertrophy: PIcon.muscle, strength: PIcon.barbell, powerbuilding: PIcon.bolt, general: PIcon.heart };
const PRESET_ICON = { gym: PIcon.barbell, dumbbells: PIcon.dumbbell, bodyweight: PIcon.body, custom: PIcon.filter };

function reduced() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

// ——— Primitives ————————————————————————————————————————————————————————————

function OptionCard({ icon: Icon, hue = 'hypertrophy', title, sub, selected, onClick, index = 0, right }) {
  return (
    <button type="button" className="llp-anim" onClick={onClick} aria-pressed={!!selected} style={{
      display: 'flex', alignItems: 'center', gap: 14, width: '100%', minHeight: 76, padding: '12px 14px',
      borderRadius: LL.r.md, textAlign: 'left', cursor: 'pointer',
      background: selected ? `color-mix(in oklab, ${LL.accent} 10%, ${LL.surface})` : LL.surface,
      border: `1.5px solid ${selected ? LL.accent : LL.line}`,
      boxShadow: selected ? `0 0 0 4px color-mix(in oklab, ${LL.accent} 16%, transparent)` : 'none',
      transition: `background .2s ${LL.ease}, border-color .2s ${LL.ease}, box-shadow .2s ${LL.ease}`,
      animation: reduced() ? undefined : `llpFadeUp .34s ${LL.ease} both ${index * 45}ms`,
    }}>
      {Icon && <GoalTile goal={hue} icon={Icon} size={46} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.lg, fontWeight: 600, color: LL.fg }}>{title}</div>
        {sub && <div style={{ ...meta, marginTop: 2, lineHeight: 1.35 }}>{sub}</div>}
      </div>
      {right}
      {/* One selection affordance per card: cards with their own indicator
          (experience level bars) skip the radio. */}
      {!right && <LLCheck on={!!selected} style={{ animation: selected && !reduced() ? `llpPop .3s ${LL.spring}` : undefined }} />}
    </button>
  );
}

function Chip({ active, onClick, children, big, grow }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={!!active} style={{
      height: big ? 56 : 42, minWidth: big ? 56 : undefined, padding: big || grow ? '0 6px' : '0 16px', borderRadius: big ? LL.r.md : LL.r.pill,
      background: active ? LL.accent : LL.surface, color: active ? LL.accentInk : LL.fg,
      border: `1.5px solid ${active ? LL.accent : LL.line}`,
      fontFamily: big ? LL.display : LL.ui, fontSize: big ? LL.fs.stat : LL.fs.meta, fontWeight: big ? 700 : 600,
      fontVariantNumeric: 'tabular-nums', cursor: 'pointer', flex: big || grow ? 1 : undefined, whiteSpace: 'nowrap',
      transition: `background .18s ${LL.ease}, border-color .18s ${LL.ease}, color .18s ${LL.ease}, transform .18s ${LL.spring}`,
      transform: active && !reduced() ? 'scale(1.03)' : 'none',
    }}>{children}</button>
  );
}

function PrimaryButton({ children, onClick, disabled, loading }) {
  return (
    <button type="button" onClick={disabled || loading ? undefined : onClick} disabled={disabled} style={{
      width: '100%', height: 56, borderRadius: LL.r.md, border: 'none',
      background: disabled ? LL.surface3 : LL.accent, color: disabled ? LL.fgMute : LL.accentInk,
      fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      transition: `background .2s ${LL.ease}, color .2s ${LL.ease}`,
    }}>{loading ? 'Setting up…' : children}</button>
  );
}

function TextButton({ children, onClick, style = {} }) {
  return (
    <button type="button" onClick={onClick} style={{
      background: 'none', border: 'none', padding: '10px 8px', cursor: 'pointer',
      fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, color: LL.fgMute, ...style,
    }}>{children}</button>
  );
}

function StepTitle({ kickerText, title, sub }) {
  return (
    <div style={{ marginBottom: 20 }}>
      {kickerText && <div style={{ ...kicker, color: LL.accentText, marginBottom: 8 }}>{kickerText}</div>}
      <div style={{ fontFamily: LL.display, fontSize: LL.fs.h2, fontWeight: 700, color: LL.fg, letterSpacing: -0.7, lineHeight: 1.05 }}>{title}</div>
      {sub && <div style={{ ...meta, fontSize: LL.fs.body, marginTop: 8, lineHeight: 1.45 }}>{sub}</div>}
    </div>
  );
}

// Decorative hero: an abstract loaded barbell with rising bars behind it. No data.
function HeroArt() {
  return (
    <svg viewBox="0 0 320 150" width="100%" height="100%" preserveAspectRatio="xMidYMax meet" aria-hidden style={{ display: 'block', maxHeight: 240, minHeight: 150 }}>
      <defs>
        <linearGradient id="llob-g" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="var(--ll-accent, oklch(0.86 0.19 132))" stopOpacity="0.55" />
          <stop offset="1" stopColor="var(--ll-accent, oklch(0.86 0.19 132))" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      {[38, 52, 46, 66, 60, 80, 74, 96, 90, 112].map((h, i) => (
        <rect key={i} className="llp-anim" x={14 + i * 30} y={136 - h} width="18" height={h} rx="5" fill="url(#llob-g)"
          style={{ transformOrigin: `${23 + i * 30}px 136px`, animation: reduced() ? undefined : `llobRise .7s ${LL.ease} both ${120 + i * 50}ms` }} />
      ))}
      <g className="llp-anim" style={{ animation: reduced() ? undefined : `llpFadeUp .5s ${LL.ease} both 500ms` }}>
        <rect x="30" y="64" width="260" height="8" rx="4" fill="var(--ll-fg, #eee)" opacity="0.9" />
        <rect x="58" y="38" width="16" height="60" rx="4" fill="var(--ll-accent, oklch(0.86 0.19 132))" />
        <rect x="78" y="46" width="12" height="44" rx="4" fill="var(--ll-accent, oklch(0.86 0.19 132))" opacity="0.75" />
        <rect x="246" y="38" width="16" height="60" rx="4" fill="var(--ll-accent, oklch(0.86 0.19 132))" />
        <rect x="230" y="46" width="12" height="44" rx="4" fill="var(--ll-accent, oklch(0.86 0.19 132))" opacity="0.75" />
      </g>
    </svg>
  );
}

// ——— WelcomeScreen ———————————————————————————————————————————————————————————

// Onboarding answers survive a reload (same tab): the user resumes at the step they were on.
const DRAFT_KEY = 'liftlog.onboarding';
function loadDraft() {
  try { const d = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null'); return d && typeof d === 'object' && STEPS.includes(d.step) ? d : null; } catch { return null; }
}
function clearDraft() { try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* private mode */ } }

function WelcomeScreen() {
  const { state, actions, replaceRoute, setRoute, toast } = useApp();
  const start = useStartTemplate();
  const p0 = state.profile || {};
  const d0 = React.useMemo(loadDraft, []) || {};
  const [step, setStep] = React.useState(d0.step || 'intro');
  const [dir, setDir] = React.useState(1);
  const [name, setName] = React.useState(d0.name ?? (state.user.name || ''));
  const [goal, setGoal] = React.useState(d0.goal ?? (p0.goal || null));
  const [experience, setExperience] = React.useState(d0.experience ?? (p0.experience || null));
  const [days, setDays] = React.useState(d0.days ?? (p0.daysPerWeek || 3));
  const [minutes, setMinutes] = React.useState(d0.minutes ?? (p0.sessionMinutes || 60));
  const [preset, setPreset] = React.useState(d0.preset ?? (p0.equipmentPreset || null));
  const [customEquip, setCustomEquip] = React.useState(() => d0.customEquip ?? (p0.equipmentPreset === 'custom' ? p0.equipment || [] : ['dumbbell', 'bench']));
  const [units, setUnits] = React.useState(d0.units ?? (state.prefs?.units || 'lb'));
  const [injuryChips, setInjuryChips] = React.useState(d0.injuryChips ?? []);
  const [injuryText, setInjuryText] = React.useState(d0.injuryText ?? '');
  React.useEffect(() => {
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ step, name, goal, experience, days, minutes, preset, customEquip, units, injuryChips, injuryText })); } catch { /* private mode */ }
  }, [step, name, goal, experience, days, minutes, preset, customEquip, units, injuryChips, injuryText]);
  const [detail, setDetail] = React.useState(null);
  const [busy, setBusy] = React.useState(null); // template id being started
  const advanceTimer = React.useRef(null);
  const settleTimer = React.useRef(null);
  const [settling, setSettling] = React.useState(false);
  React.useEffect(() => () => { clearTimeout(advanceTimer.current); clearTimeout(settleTimer.current); }, []);

  const equipment = preset === 'custom' ? customEquip : (preset ? equipmentForPreset(preset) : null);
  const injuries = [...injuryChips, injuryText.trim()].filter(Boolean).join(', ');
  const profile = {
    ...(goal ? { goal } : {}),
    ...(experience ? { experience } : {}),
    daysPerWeek: days, sessionMinutes: minutes,
    ...(preset ? { equipmentPreset: preset, equipment } : {}),
    injuries,
  };

  const go = (to, d = 1) => { setDir(d); setStep(to); };
  // Single-select steps auto-advance AND show Continue in the same spot. A
  // Continue tap cancels a pending auto-advance (one step, not two). After an
  // auto-advance, only a tap whose finger went down BEFORE the step changed is
  // dropped (it was meant for the previous question); a fresh tap on the new
  // step always counts, however quick.
  const stepRef = React.useRef(step);
  stepRef.current = step;
  const stepAt = React.useRef(0);
  const autoAt = React.useRef(-1);
  const lastDown = React.useRef(0);
  React.useEffect(() => { stepAt.current = Date.now(); }, [step]);
  const advance = (from, auto = false) => {
    if (stepRef.current !== from) return; // already moved on
    clearTimeout(advanceTimer.current);
    const i = STEPS.indexOf(from);
    const to = STEPS[i + 1];
    if (to === 'plan') commitProfile();
    if (auto) {
      autoAt.current = Date.now();
      // Arriving by auto-advance: the next step's Continue fades in and ignores
      // taps for a moment, so a habitual "Continue" meant for the previous
      // question can't skip this one.
      setSettling(true);
      clearTimeout(settleTimer.current);
      settleTimer.current = setTimeout(() => setSettling(false), 900);
    }
    go(to, 1);
  };
  const next = () => {
    const staleTap = autoAt.current >= stepAt.current - 50 && lastDown.current > 0
      && lastDown.current < stepAt.current && Date.now() - lastDown.current < 1000;
    if (staleTap) return;
    advance(step);
  };
  const back = () => {
    if (step === 'browse') return go('plan', -1);
    const i = STEPS.indexOf(step);
    go(STEPS[Math.max(0, i - 1)], -1);
  };
  const autoNext = () => {
    clearTimeout(advanceTimer.current);
    const from = step;
    advanceTimer.current = setTimeout(() => advance(from, true), reduced() ? 0 : 260);
  };

  const commitProfile = () => {
    actions.setUserName(name);
    actions.setProfile(profile);
    actions.setPref('units', units);
    // Simple mode (plain words, no effort columns) starts on for people new to
    // lifting and off for everyone else; Settings → Progression switches it.
    if (experience) actions.setPref('simpleMode', experience === 'new' || experience === 'beginner');
  };
  const finish = (route = 'home', params = {}) => {
    clearDraft();
    actions.markWelcomed();
    replaceRoute(route, params);
  };
  const startTemplate = async (t) => {
    if (busy) return;
    setBusy(t.id);
    try {
      commitProfile();
      await start(t, { profile, units });
      setDetail(null);
      finish('home');
    } finally { setBusy(null); }
  };
  const buildOwn = () => {
    commitProfile();
    const used = SCHEDULE[days] || SCHEDULE[3];
    const id = actions.addProgram({
      name: 'My program', author: 'You', weeks: 8, currentWeek: 1, split: 'Custom',
      days: used.map((d, i) => ({ id: 'day_' + Math.random().toString(36).slice(2, 8), name: `Day ${i + 1}`, day: d, exercises: [] })),
    });
    finish('program-edit', { programId: id });
  };
  const justLog = () => {
    commitProfile();
    finish('home');
    toast('Start an empty workout from Home whenever you want to log');
  };
  const pickClassic = (id) => {
    actions.setUserName(name);
    actions.pickStarterProgram(id);
    finish('home');
  };

  const qIndex = QUESTION_STEPS.indexOf(step);
  const showChrome = step !== 'intro';
  const anim = reduced() ? undefined : `${dir > 0 ? 'llpSlideIn' : 'llpSlideBack'} .32s ${LL.ease} both`;

  // Footer CTA per step (single-select steps auto-advance, but still get a Continue).
  let footer = null;
  if (step === 'intro') {
    footer = (
      <>
        <PrimaryButton onClick={next}>Set up my plan <LLIcon.chev style={{ width: 18, height: 18 }} /></PrimaryButton>
        <TextButton onClick={() => go('browse', 1)} style={{ width: '100%', marginTop: 4 }}>Skip setup and pick a starter program</TextButton>
        <TextButton onClick={() => setRoute('settings/sync', { signin: true })} style={{ width: '100%', marginTop: 0 }}>I already use Lift Log on another device</TextButton>
      </>
    );
  } else if (step === 'schedule' || step === 'injuries' || (step === 'equipment' && preset === 'custom')) {
    // Single-select steps (goal, experience, a preset, units) advance on the tap
    // itself and have no Continue: a habitual "select, then Continue" could
    // otherwise land on the NEXT question's button and skip it.
    footer = <PrimaryButton onClick={next}>{step === 'injuries' ? (injuries ? 'Show my plan' : 'None — show my plan') : 'Continue'}</PrimaryButton>;
  } else if (QUESTION_STEPS.includes(step)) {
    footer = (
      <div style={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgDim }}>
        Tap an answer to continue
      </div>
    );
  }

  return (
    <div onPointerDownCapture={() => { lastDown.current = Date.now(); }} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: LL.bg, position: 'relative' }}>
      <ProgramStyles />
      <style>{`@keyframes llobRise { from { transform: scaleY(0); opacity: 0; } to { transform: scaleY(1); opacity: 1; } }`}</style>

      {/* Top bar */}
      <div style={{ padding: '48px 16px 8px', display: 'flex', alignItems: 'center', gap: 10, minHeight: 40 }}>
        {showChrome ? (
          <BackButton onClick={back} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: LL.r.md, background: LL.accent, color: LL.accentInk, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LLIcon.dumbbell style={{ width: 22, height: 22 }} />
            </div>
            <div style={{ fontFamily: LL.display, fontSize: LL.fs.h3, fontWeight: 700, color: LL.fg, letterSpacing: -0.3 }}>Lift Log</div>
          </div>
        )}
        <div style={{ flex: 1, display: 'flex', gap: 4, padding: '0 6px' }} aria-hidden={!showChrome}>
          {showChrome && step !== 'browse' && [...QUESTION_STEPS, 'plan'].map((s, i) => {
            const cur = step === 'plan' ? QUESTION_STEPS.length : qIndex;
            return (
              <div key={s} style={{ flex: 1, height: 4, borderRadius: LL.r.xs, background: LL.surface3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: i <= cur ? '100%' : '0%', background: LL.accent, borderRadius: LL.r.xs, transition: `width .35s ${LL.ease}` }} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '8px 20px 20px' }}>
        <div key={step} className="llp-anim" style={{ animation: anim, ...(step === 'intro' ? { minHeight: '100%', display: 'flex', flexDirection: 'column' } : {}) }}>
          {step === 'intro' && (
            <div style={{ paddingTop: 12, flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* The art takes whatever height the phone has spare, so the copy and name field sit on the CTA. */}
              <div style={{ flex: 1, minHeight: 150, display: 'flex', alignItems: 'flex-end' }}><HeroArt /></div>
              <div style={{ fontFamily: LL.display, fontSize: LL.fs.big, fontWeight: 700, color: LL.fg, letterSpacing: -1.2, lineHeight: 0.98, marginTop: 18 }}>
                Every rep.<br />Every PR.<br /><span style={{ color: LL.accentText }}>A plan that fits.</span>
              </div>
              <div style={{ ...meta, fontSize: LL.fs.body, marginTop: 14, lineHeight: 1.5 }}>
                Answer six quick questions and get a program matched to your goal, schedule and equipment. Everything stays on this phone — no account.
              </div>
              <label htmlFor="llob-name" style={{ ...kicker, display: 'block', marginTop: 26, marginBottom: 8 }}>What should we call you?</label>
              <input
                id="llob-name" type="text" value={name} placeholder="First name (optional)" maxLength={40} autoComplete="given-name"
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') next(); }}
                style={{
                  width: '100%', height: 54, padding: '0 16px', borderRadius: LL.r.md, boxSizing: 'border-box',
                  background: LL.surface, border: `1.5px solid ${LL.line}`, color: LL.fg,
                  fontFamily: LL.ui, fontSize: LL.fs.lg, outline: 'none',
                }}
                onFocus={(e) => { e.target.style.borderColor = LL.accent; }}
                onBlur={(e) => { e.target.style.borderColor = LL.line; }}
              />
            </div>
          )}

          {step === 'goal' && (
            <>
              <StepTitle kickerText={`Step 1 of ${QUESTION_STEPS.length}`} title={name ? `What's the goal, ${name.split(' ')[0]}?` : "What's your main goal?"} sub="We'll weight your plan toward it. You can change this any time." />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {GOALS.map((g, i) => (
                  <OptionCard key={g.id} index={i} icon={GOAL_ICON[g.id]} hue={g.id} title={g.label} sub={g.sub} selected={goal === g.id}
                    onClick={() => { setGoal(g.id); autoNext(); }} />
                ))}
              </div>
            </>
          )}

          {step === 'experience' && (
            <>
              <StepTitle kickerText={`Step 2 of ${QUESTION_STEPS.length}`} title="How experienced are you?" sub="Sets your starting volume and weights, so a first session is never a 225 lb deadlift." />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {LEVELS.map((l, i) => (
                  <OptionCard key={l.id} index={i} title={l.label} sub={l.sub} selected={experience === l.id}
                    onClick={() => { setExperience(l.id); autoNext(); }}
                    right={<LevelBars n={i + 1} on={experience === l.id} />} />
                ))}
              </div>
            </>
          )}

          {step === 'schedule' && (
            <>
              <StepTitle kickerText={`Step 3 of ${QUESTION_STEPS.length}`} title="How often can you train?" sub="Pick what you can keep up on a normal week, not your best week." />
              <div style={{ ...kicker, marginBottom: 10 }}>Days per week</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[2, 3, 4, 5, 6].map(d => <Chip key={d} big active={days === d} onClick={() => setDays(d)}>{d}</Chip>)}
              </div>
              <div style={{ background: LL.surface, border: `1px solid ${LL.line}`, borderRadius: LL.r.md, padding: 14, marginTop: 12 }}>
                <SplitStrip days={SCHEDULE[days]} size="lg" />
                <div style={{ ...meta, marginTop: 10 }}>
                  {days <= 3 ? 'Full-body sessions work best at this frequency.' : days === 4 ? 'Ideal for an upper / lower split.' : 'Enough room for a split that trains each muscle twice.'}
                </div>
              </div>
              <div style={{ ...kicker, margin: '24px 0 10px' }}>Time per session</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {MINUTES.map(m => <Chip key={m} grow active={minutes === m} onClick={() => setMinutes(m)}>{m === 90 ? '90+' : m}{' '}<span style={{ fontSize: LL.fs.sm, fontWeight: 500, opacity: 0.8 }}>min</span></Chip>)}
              </div>
            </>
          )}

          {step === 'equipment' && (
            <>
              <StepTitle kickerText={`Step 4 of ${QUESTION_STEPS.length}`} title="Where do you train?" sub="We'll only suggest programs you can actually do." />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {EQUIPMENT_PRESETS.map((p, i) => (
                  <OptionCard key={p.id} index={i} icon={PRESET_ICON[p.id]} hue={p.id === 'gym' ? 'strength' : p.id === 'bodyweight' ? 'general' : 'hypertrophy'}
                    title={p.label} sub={p.sub} selected={preset === p.id}
                    onClick={() => { setPreset(p.id); if (p.id !== 'custom') autoNext(); }} />
                ))}
              </div>
              {preset === 'custom' && (
                <div className="llp-anim" style={{ marginTop: 16, animation: reduced() ? undefined : `llpFadeUp .25s ${LL.ease} both` }}>
                  <div style={{ ...kicker, marginBottom: 10 }}>I have access to</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {EQUIPMENT_TAGS.map(t => (
                      <Chip key={t.id} active={customEquip.includes(t.id)}
                        onClick={() => setCustomEquip(cur => cur.includes(t.id) ? cur.filter(x => x !== t.id) : [...cur, t.id])}>{t.label}</Chip>
                    ))}
                  </div>
                  {customEquip.length === 0 && <div style={{ ...meta, marginTop: 10 }}>Nothing selected — we'll suggest bodyweight programs.</div>}
                </div>
              )}
            </>
          )}

          {step === 'units' && (
            <>
              <StepTitle kickerText={`Step 5 of ${QUESTION_STEPS.length}`} title="Pounds or kilograms?" sub="Used everywhere you see a weight. Switch any time in Settings." />
              <div style={{ display: 'flex', gap: 10 }}>
                {[{ id: 'lb', big: 'lb', sub: 'Pounds', ex: '45 lb bar' }, { id: 'kg', big: 'kg', sub: 'Kilograms', ex: '20 kg bar' }].map((u, i) => {
                  const on = units === u.id;
                  return (
                    <button key={u.id} type="button" className="llp-anim" aria-pressed={on} onClick={() => { setUnits(u.id); autoNext(); }} style={{
                      flex: 1, height: 168, borderRadius: LL.r.lg, cursor: 'pointer',
                      background: on ? `color-mix(in oklab, ${LL.accent} 10%, ${LL.surface})` : LL.surface,
                      border: `1.5px solid ${on ? LL.accent : LL.line}`,
                      boxShadow: on ? `0 0 0 4px color-mix(in oklab, ${LL.accent} 16%, transparent)` : 'none',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                      animation: reduced() ? undefined : `llpFadeUp .34s ${LL.ease} both ${i * 60}ms`,
                      transition: `background .2s ${LL.ease}, border-color .2s ${LL.ease}`,
                    }}>
                      <span style={{ fontFamily: LL.display, fontSize: LL.fs.hero, fontWeight: 700, color: on ? LL.accentText : LL.fg, lineHeight: 1 }}>{u.big}</span>
                      <span style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{u.sub}</span>
                      <span style={{ ...meta, fontSize: LL.fs.sm }}>{u.ex}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {step === 'injuries' && (
            <>
              <StepTitle kickerText={`Step 6 of ${QUESTION_STEPS.length} · optional`} title="Anything we should work around?" sub="Old injuries or movements that bother you. The AI coach uses this when it suggests changes." />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {INJURY_CHIPS.map(c => (
                  <Chip key={c} active={injuryChips.includes(c)} onClick={() => setInjuryChips(cur => cur.includes(c) ? cur.filter(x => x !== c) : [...cur, c])}>{c}</Chip>
                ))}
              </div>
              <textarea
                aria-label="Injuries or limitations"
                value={injuryText}
                onChange={(e) => setInjuryText(e.target.value)}
                placeholder="Anything else? e.g. avoid overhead pressing, recovering from a knee sprain"
                rows={3}
                maxLength={300}
                style={{
                  marginTop: 14, width: '100%', boxSizing: 'border-box', padding: '14px 16px', borderRadius: LL.r.md, resize: 'none',
                  background: LL.surface, border: `1.5px solid ${LL.line}`, color: LL.fg,
                  fontFamily: LL.ui, fontSize: LL.fs.body, lineHeight: 1.45, outline: 'none',
                }}
              />
            </>
          )}

          {step === 'plan' && (
            <PlanStep name={name} profile={profile} onOpen={setDetail} onStart={startTemplate} busy={busy}
              onBrowse={() => go('browse', 1)} onBuildOwn={buildOwn} onJustLog={justLog}
              onCoach={() => { commitProfile(); finish('ai-coach', { ask: coachProgramAsk(profile) }); }} />
          )}

          {step === 'browse' && (
            <BrowseStep profile={(goal || experience || preset) ? profile : null} onOpen={setDetail} onClassic={pickClassic} />
          )}
        </div>
      </div>

      {footer && (
        <div style={{ padding: '10px 20px calc(18px + env(safe-area-inset-bottom))', background: LL.bg,
          opacity: settling ? 0 : 1, pointerEvents: settling ? 'none' : 'auto', transition: `opacity ${LL.motion.base}ms ${LL.ease}` }}>
          {footer}
        </div>
      )}

      <TemplateDetailSheet template={detail} open={!!detail} onClose={() => setDetail(null)}
        onStart={startTemplate} starting={!!busy} profile={(goal || experience || preset) ? profile : null} />
    </div>
  );
}

function LevelBars({ n, on }) {
  return (
    <div aria-hidden style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 22, marginRight: 4 }}>
      {[1, 2, 3, 4].map(i => (
        <div key={i} style={{
          width: 5, height: 6 + i * 4, borderRadius: LL.r.xs,
          background: i <= n ? (on ? LL.accent : LL.fgMute) : LL.surface3,
          transition: `background .2s ${LL.ease}`,
        }} />
      ))}
    </div>
  );
}

// ——— "Your plan" ———————————————————————————————————————————————————————————

function PlanStep({ name, profile, onOpen, onStart, busy, onBrowse, onBuildOwn, onJustLog, onCoach }) {
  const aiMode = useAIStatus().mode;
  const ranked = React.useMemo(() => rankPlans(profile), [profile.goal, profile.experience, profile.daysPerWeek, profile.sessionMinutes, (profile.equipment || []).join(), profile.injuries]); // eslint-disable-line react-hooks/exhaustive-deps
  const fits = ranked.filter(r => r.equipOk);
  const top = fits[0] || ranked[0];
  const more = fits.slice(1, 3);
  const first = name ? name.split(' ')[0] : '';

  return (
    <div>
      <StepTitle kickerText="Your plan" title={first ? `Here's your plan, ${first}` : "Here's your plan"} sub="Matched to your answers. Start it now, look inside first, or take another route below." />

      {top && (
        <div>
          <TemplateCard template={top.template} featured badge="Best match" reasons={top.reasons} warnings={top.warnings} onOpen={onOpen} />
          {top.why && <WhyThis why={top.why} />}
          <button type="button" onClick={() => onStart(top.template)} disabled={!!busy} style={{
            width: '100%', marginTop: 10, height: 56, borderRadius: LL.r.md, border: 'none', background: LL.accent, color: LL.accentInk,
            fontFamily: LL.ui, fontSize: LL.fs.btn, fontWeight: 600, cursor: 'pointer',
          }}>{busy === top.template.id ? 'Setting up…' : 'Start this program'}</button>
          <TextButton onClick={() => onOpen(top.template)} style={{ width: '100%', marginTop: 2 }}>Look inside first</TextButton>
        </div>
      )}

      {more.length > 0 && (
        <div style={{ marginTop: 26 }}>
          <div style={{ ...kicker, margin: '0 2px 10px' }}>Also a good fit</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {more.map((r, i) => <TemplateCard key={r.template.id} index={i + 1} template={r.template} reasons={r.reasons.slice(0, 2)} onOpen={onOpen} />)}
          </div>
        </div>
      )}

      <div style={{ marginTop: 26 }}>
        <div style={{ ...kicker, margin: '0 2px 10px' }}>Or</div>
        <div style={{ background: LL.surface, border: `1px solid ${LL.line}`, borderRadius: LL.r.md, overflow: 'hidden' }}>
          {[
            { id: 'browse', icon: PIcon.layers, title: `Browse all ${TEMPLATES.length} programs`, sub: 'Filter by days, goal and equipment', onClick: onBrowse },
            { id: 'coach', icon: PIcon.sparkle, title: 'Have the AI coach build one', sub: aiMode === 'off' ? 'Uses your answers · needs a connection' : aiMode === 'byo' ? 'Uses your answers · on your API key' : 'Uses your answers · free', onClick: onCoach },
            { id: 'own', icon: PIcon.edit, title: 'Build my own', sub: 'Start from empty days and add exercises', onClick: onBuildOwn },
            { id: 'log', icon: LLIcon.note, title: 'Just log workouts', sub: 'No program — log whatever you train', onClick: onJustLog },
          ].map((o, i) => (
            <button key={o.id} type="button" onClick={o.onClick} style={{
              display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 64, padding: '10px 14px',
              background: 'none', border: 'none', borderTop: i ? `1px solid ${LL.line}` : 'none', textAlign: 'left', cursor: 'pointer',
            }}>
              <div style={{ width: 36, height: 36, borderRadius: LL.r.md, background: LL.surface2, color: LL.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <o.icon style={{ width: 18, height: 18 }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{o.title}</div>
                <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 1 }}>{o.sub}</div>
              </div>
              <LLIcon.chev style={{ color: LL.fgFaint }} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// "Why this program" — the matcher's reasoning in plain words.
function WhyThis({ why }) {
  return (
    <div className="llp-anim" style={{
      marginTop: 10, padding: '14px 16px', borderRadius: LL.r.md, background: LL.surface, border: `1px solid ${LL.line}`,
      animation: reduced() ? undefined : `llpFadeUp .34s ${LL.ease} both 120ms`,
    }}>
      <div style={{ ...kicker, color: LL.accentText, marginBottom: 8 }}>{why.headline}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {why.lines.map((l, i) => {
          const m = /^How you progress: (.*)$/.exec(l);
          return (
            <p key={i} style={{ margin: 0, fontFamily: LL.ui, fontSize: LL.fs.meta, lineHeight: 1.5, color: i === 0 ? LL.fg : LL.fgMute }}>
              {m ? <><b style={{ color: LL.fg, fontWeight: 600 }}>How you progress.</b> {m[1]}</> : l}
            </p>
          );
        })}
      </div>
    </div>
  );
}

// ——— Browse (all programs) ————————————————————————————————————————————————

function BrowseStep({ profile, onOpen, onClassic }) {
  const list = React.useMemo(() => (profile ? rankPlans(profile).map(r => r.template) : TEMPLATES), [profile]);
  const [days, setDays] = React.useState(null);
  const shown = list.filter(t => !days || t.daysPerWeek === days);
  return (
    <div>
      <StepTitle kickerText="Program library" title="Pick a starter program" sub="Tap any program to see every workout before you start." />
      <div className="llp-noscroll" style={{ display: 'flex', gap: 8, overflowX: 'auto', margin: '0 -20px 14px', padding: '0 20px', scrollbarWidth: 'none' }}>
        <Chip active={!days} onClick={() => setDays(null)}>All</Chip>
        {[2, 3, 4, 5, 6].map(d => <Chip key={d} active={days === d} onClick={() => setDays(days === d ? null : d)}>{d} days</Chip>)}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {shown.map((t, i) => <TemplateCard key={t.id} index={i} template={t} onOpen={onOpen} />)}
      </div>
      <div style={{ ...kicker, margin: '28px 2px 6px' }}>Classic starters</div>
      <div style={{ ...meta, margin: '0 2px 10px', lineHeight: 1.4 }}>Pre-filled with example weights for an experienced lifter. Adjust them after your first session.</div>
      <div style={{ background: LL.surface, border: `1px solid ${LL.line}`, borderRadius: LL.r.md, overflow: 'hidden' }}>
        {(STARTER_PROGRAMS || []).map((s, i) => (
          <button key={s.id} type="button" onClick={() => onClassic(s.id)} style={{
            display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 60, padding: '10px 14px',
            background: 'none', border: 'none', borderTop: i ? `1px solid ${LL.line}` : 'none', textAlign: 'left', cursor: 'pointer',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{s.label}</div>
              <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 1 }}>{s.blurb}</div>
            </div>
            <LLIcon.chev style={{ color: LL.fgFaint }} />
          </button>
        ))}
      </div>
    </div>
  );
}

export { WelcomeScreen };
